<template>
    <flex-container :class="componentClasses" :gap="Size.sm">
        <text-decorator>
            {{ props.title }}
        </text-decorator>
        <flex-container
            align-items="center"
            :gap="Size.sm"
        >
            <text-decorator bold :size="FontSize.xl">
                <template v-if="renderType === 'count'">
                    {{ props.currentValue }}
                </template>
                <template v-if="renderType === 'percentage'">
                    {{ props.currentValue }}&percnt;
                </template>
            </text-decorator>

            <div class="statistics-entry__content">
                <div class="statistics-entry__icon">
                    <svg-icon
                        :icon="differenceIcon"
                        :color="differenceColor"
                        :size="IconSize.sm"
                        is-custom-icon
                    />
                </div>
                <div v-if="differencePercentage !== undefined" class="statistics-entry__difference">
                    <text-decorator :size="FontSize.xs">
                        {{ renderPercentage(differencePercentage) }}
                    </text-decorator>
                </div>
            </div>
        </flex-container>

        <text-decorator :size="FontSize.xs">
            <slot
                name="difference-text"
                :current-value="props.currentValue"
                :previous-value="props.previousValue"
            />
        </text-decorator>
    </flex-container>
</template>

<script setup lang="ts">
    import { computed } from 'vue';
    import { FontSize, IconSize, Size } from '@keyvue/one-ui';
    import type StatisticRenderType from '@/flow/statistics/types/StatisticRenderType';
    import IconArrowUp from '@keyvue/one-awesome-icons/icons/regular/arrow-up.svg?component';
    import IconArrowDown from '@keyvue/one-awesome-icons/icons/regular/arrow-down.svg?component';
    import IconEquals from '@keyvue/one-awesome-icons/icons/regular/equals.svg?component';

    interface Props {
        title: string
        renderType: StatisticRenderType
        currentValue: number
        previousValue?: number
        variant?: 'default' | 'flow-card'
    }

    const props = withDefaults(defineProps<Props>(), {
        previousValue: 0,
        variant: 'default',
    });

    const differencePercentage = computed(() => {
        if (props.previousValue === 0) {
            return undefined;
        }

        return (((props.currentValue - props.previousValue) / props.previousValue) * 100);
    });

    const isPositive = computed(() => props.currentValue > props.previousValue);
    const isNegative = computed(() => props.currentValue < props.previousValue);
    const isNeutral = computed(() => props.currentValue === props.previousValue);

    const differenceIcon = computed(() => {
        switch (true) {
            case isPositive.value: return IconArrowUp;
            case isNegative.value: return IconArrowDown;
            default: return IconEquals;
        }
    });

    const differenceColor = computed(() => {
        switch (true) {
            case isPositive.value: return 'green-500';
            case isNegative.value: return 'orange-500';
            default: return 'primary';
        }
    });

    function renderPercentage(value: number) {
        return `${Math.abs(value).toFixed(0)}%`;
    }

    const componentClasses = computed(() => {
        return {
            'statistics-entry': true,
            [`statistics-entry--${props.variant}`]: true,
            'statistics-entry--positive': isPositive.value,
            'statistics-entry--negative': isNegative.value,
            'statistics-entry--neutral': isNeutral.value,
        };
    });
</script>

<style scoped lang="scss">
.statistics-entry {
    padding: size(sm);

    &--flow-card {
        padding: size(md);
        position: absolute;
        top: 0;
        color: white;

        .statistics-entry__icon:before {
            content: unset;
        }

        .statistics-entry__content {
            display: flex;
            align-items: center;
            gap: 0;
            background-color: white;
            border-radius: size(md);
            padding: size(xs) size(sm);
        }
    }

    &__content {
        display: flex;
        align-items: center;
        gap: size(sm);
    }

    &__icon {
        padding: 2px;
        border-radius: 50%;
        width: size(sm);
        height: size(sm);
        position: relative;
        display: flex;
        align-items: center;
        justify-content: center;

        &:before {
            content: '';
            width: 100%;
            height: 100%;
            position: absolute;
            opacity: 0.2;
            border-radius: 50%;
        }
    }

    &--positive {
        .statistics-entry__difference {
            color: #3F8824;
        }

        .statistics-entry__icon:before {
            background-color: #3F8824;
        }
    }

    &--negative {
        .statistics-entry__difference {
            color: #ED6E2D;
        }

        .statistics-entry__icon:before {
            background-color: #ED6E2D;
        }
    }

    &--neutral {
        .statistics-entry__difference {
            color: var(--theme-colors-default--primary);
        }

        .statistics-entry__icon:before {
            background-color: var(--theme-colors-default--primary);
        }
    }
}
</style>
